import CableApp from 'actioncable'

// Feed ticks are signal-only cable messages ("the feed head moved"); content
// never rides the cable. See docs/feed-new-items-pill.md.
export interface FeedTick {
    t?: number
}

type TickListener = (tick: FeedTick) => void

const FEED_TICK_MESSAGE = 'feed tick'

// Following-feed ticks arrive on the authenticated UserChannel; useUserChannel
// forwards them here so pill consumers don't own any cable plumbing.
const userTickListeners = new Set<TickListener>()

export function dispatchUserFeedTick(tick: FeedTick) {
    userTickListeners.forEach(listener => {
        try {
            listener(tick)
        } catch (error) {
            console.error('>>> user feed tick listener error', error)
        }
    })
}

export function subscribeUserFeedTicks(listener: TickListener): () => void {
    userTickListeners.add(listener)
    return () => {
        userTickListeners.delete(listener)
    }
}

// Public ticks ride a dedicated broadcast channel. One refcounted subscription
// per tab no matter how many panels listen.
const publicTickListeners = new Set<TickListener>()
let publicSubscription: { unsubscribe: () => void } | null = null

function dispatchPublicFeedTick(tick: FeedTick) {
    publicTickListeners.forEach(listener => {
        try {
            listener(tick)
        } catch (error) {
            console.error('>>> public feed tick listener error', error)
        }
    })
}

// A QuestChannel update for a mounted feed card means the server-side feed may
// now rank that thread differently. Notify both feed families so each eligible
// panel can probe its own source without applying the realtime payload in place.
export function dispatchLocalFeedTick(tick: FeedTick) {
    dispatchUserFeedTick(tick)
    dispatchPublicFeedTick(tick)
}

function ensurePublicSubscription() {
    if (publicSubscription || !CableApp?.cable) return

    let connectedOnce = false
    publicSubscription = CableApp.cable.subscriptions.create(
        { channel: 'PublicFeedChannel' },
        {
            connected: () => {
                if (connectedOnce) {
                    dispatchPublicFeedTick({})
                }
                connectedOnce = true
            },
            received: (data: { message?: string; t?: number }) => {
                if (data?.message !== FEED_TICK_MESSAGE) return
                dispatchPublicFeedTick(data)
            },
        },
    )
}

export function subscribePublicFeedTicks(listener: TickListener): () => void {
    publicTickListeners.add(listener)
    ensurePublicSubscription()
    return () => {
        publicTickListeners.delete(listener)
        if (publicTickListeners.size === 0 && publicSubscription) {
            publicSubscription.unsubscribe()
            publicSubscription = null
        }
    }
}
